import { produce } from 'immer';
import type { BlockStateDefinition } from '@unifyapps/carbon/no-code/components/BlockDefinition';
import type { BlockType, ComponentTypeUnionType } from '@unifyapps/defs/types/block';
import _pick from 'lodash/pick';
import type {
  CopilotInputBlockRef,
  CopilotInputBlockStateType,
  CopilotInputComponentType,
} from '@unifyapps/defs/blocks/CopilotInput/types';
import { CopilotInputMethods } from '@unifyapps/defs/blocks/CopilotInput/types';
import { lazy } from 'react';
import type { Action } from '@unifyapps/defs/types/action';
import _map from 'lodash/map';

const importCopilotInputComponent = () =>
  import(
    /* webpackChunkName: "copilot-input-block" */
    './CopilotInput'
  );

const CopilotInput = lazy(importCopilotInputComponent);

class CopilotInputBlockStateDefinition implements BlockStateDefinition {
  useBlockRef?: boolean | undefined = true;

  get type(): ComponentTypeUnionType {
    return 'CopilotInput';
  }

  get initialStateGetter() {
    return (block: BlockType) => ({
      id: block.id,
      ..._pick(block.component as CopilotInputComponentType, [
        'content',
        'appearance',
        'componentType',
      ]),
      value: '',
    });
  }

  getEventTargetIds(block: BlockType<CopilotInputComponentType>) {
    return _map(block.component.content.actions, (action) => action.id);
  }

  getBlockControlMethods() {
    return [
      {
        id: CopilotInputMethods.SetValue,
        label: 'Set Value',
        method: (blockState: CopilotInputBlockStateType, action: Action) => {
          const { methodPayload } = action.payload as {
            methodPayload?: { content: string | undefined };
          };

          return { ...blockState, value: methodPayload?.content };
        },
      },
      {
        id: CopilotInputMethods.ChangeMode,
        label: 'Change Mode',
        method: (blockState: CopilotInputBlockStateType, action: Action) => {
          const { methodPayload } = action.payload as {
            methodPayload?: { mode: 'compact' | 'comfortable' | undefined };
          };

          return produce(blockState, (draft) => {
            if (methodPayload?.mode) {
              draft.content.mode = methodPayload.mode;
            }
          });
        },
      },
      {
        id: CopilotInputMethods.StartDictation,
        label: 'Start Dictation',
        method: (
          blockState: CopilotInputBlockStateType,
          action: Action,
          blockRef: CopilotInputBlockRef,
        ) => {
          blockRef.startDictation?.();
          return Promise.resolve();
        },
      },
      {
        id: CopilotInputMethods.StopDictation,
        label: 'Stop Dictation',
        method: (
          blockState: CopilotInputBlockStateType,
          action: Action,
          blockRef: CopilotInputBlockRef,
        ) => {
          blockRef.stopDictation?.();
          return Promise.resolve();
        },
      },
      {
        id: CopilotInputMethods.Reset,
        label: 'Reset',
        method: (
          blockState: CopilotInputBlockStateType,
          action: Action,
          blockRef: CopilotInputBlockRef,
        ) => {
          blockRef.reset?.();
          return Promise.resolve({
            ...blockState,
            value: undefined,
            attachments: [],
            content: {
              ...blockState.content,
              autoFocus: false,
            },
          });
        },
      },
    ];
  }

  getComponent() {
    return CopilotInput;
  }

  getKeysToOmitInComputation() {
    return ['value'];
  }
}

export default CopilotInputBlockStateDefinition;
